/**
 * First admin — the page `deploy/vps/install.sh` prints a link to
 * (`/admin/setup?token=…`). Creates the first account with the one-time
 * setup token, signs in, and marks setup complete.
 *
 * It did not exist (RW-4.23): the printed link fell through to the sign-in
 * page of an install with no accounts, so nobody could ever sign in. Setup
 * is completed here because a self-host install is configured in `.env`
 * (model, storage, database — install.sh refuses to run without them);
 * until it is, the backend answers 503 to everything but sign-in.
 */
import * as React from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { createClient, createConfig } from "@hey-api/client-fetch";
import { complete, login, setupAdmin } from "@/api";
import { API_BASE } from "@/api/baseUrl";
import { useAuth } from "@/auth/useAuth";
import { apiErrorMessage } from "@/lib/api-error";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

const publicClient = createClient(createConfig({ baseUrl: API_BASE }));

export function FirstAdminPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [token, setToken] = React.useState(params.get("token") ?? "");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(false);

  if (auth.ready && auth.user && !pending) return <Navigate to="/" replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await setupAdmin({
        client: publicClient,
        body: { setup_token: token.trim(), email, password },
        throwOnError: true,
      });
      const { data } = await login({ client: publicClient, body: { email, password }, throwOnError: true });
      const authed = createClient(
        createConfig({ baseUrl: API_BASE, headers: { Authorization: `Bearer ${data.token}` } }),
      );
      await complete({ client: authed, throwOnError: true });
      await auth.signIn(email, password);
      // On to the wizard: optional keys (search, email, voice …) can be
      // added now or skipped; the install already runs on its .env.
      navigate("/setup", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err));
      setPending(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg p-6" aria-labelledby="first-admin-title">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle id="first-admin-title">Create the first admin</CardTitle>
          <CardDescription>
            This install has no accounts yet. The setup token is the one install.sh printed; it works once.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-fg-muted">Setup token</span>
              <Input name="setup_token" required value={token} onChange={(e) => setToken(e.target.value)} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-fg-muted">Email</span>
              <Input
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-fg-muted">Password (at least 8 characters)</span>
              <Input
                type="password"
                name="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error ? (
              <p role="alert" className="text-xs text-danger">
                {error}
              </p>
            ) : null}
            <Button type="submit" className="w-full" size="lg" disabled={pending}>
              {pending ? "Creating…" : "Create admin and finish setup"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
